// components/daimon/daimonScreen.ts
// What the Daimon's cursor finds on the user's screen. She works through the interface as a user
// would, so these read the page the user sees rather than any module's state.

const visible = (node: Element) => {
  const rect = node.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && getComputedStyle(node).visibility !== 'hidden';
};

/** Her own page and the left bar are never what she operates inside a module. */
const outsideWork = (node: Element) => {
  if (node.closest('[role="region"][aria-label="Daimon"], [data-daimon-ui]')) return true;
  const sidebar = sidebarOf();
  return Boolean(sidebar?.contains(node));
};

/** The whole left bar (its aside), Recents included: an inner column of it is not enough. */
const sidebarOf = () => document.getElementById('portal-nav-home')?.closest<HTMLElement>('aside') ?? null;

/**
 * The module's own assistant box on screen. Only a field marked data-assistant-composer: the pages
 * also hold editable text that is the user's work (a Slides review stamp is contenteditable), and
 * typing there would overwrite it.
 */
export const findAssistantComposer = (): HTMLElement | null =>
  [...document.querySelectorAll<HTMLElement>('[data-assistant-composer]')]
    .find((field) => visible(field) && !outsideWork(field) && !field.closest('[role="dialog"]'))
  // A tool built later whose box is not marked yet: its message box, by what it says it is.
  ?? [...document.querySelectorAll<HTMLElement>('textarea, [role="textbox"][contenteditable="true"]')]
    .find((field) => visible(field) && !outsideWork(field) && !field.closest('[role="dialog"]')
      && /message|ask|describe|demande|écri|instruction/i.test(`${field.getAttribute('aria-label') ?? ''} ${field.getAttribute('placeholder') ?? ''}`))
  ?? null;

/** Presses Enter in the field, the way every composer here sends. */
export const pressEnter = (field: HTMLElement) => {
  for (const type of ['keydown', 'keypress', 'keyup']) {
    field.dispatchEvent(new KeyboardEvent(type, { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true }));
  }
};

// Anything that takes something away from the user asks first, the trash included: 'Move to trash'
// passed unchecked until 2026-09-29.
const DANGEROUS = /delete|remove|supprim|archive|stop|arrêt|dismiss|trash|corbeille|poubelle|discard|clear|effacer/i;

/**
 * The item on screen whose name matches (a campaign, a project, a deck): the smallest visible text
 * holding the words, lifted to what a click opens. A control that deletes or stops is never picked.
 */
const fold = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const findItem = (name: string): HTMLElement | null => {
  // Every word of the name, in any order, accents aside: people say names loosely.
  // A lone digit counts: "Untitled Project (6)" is not "Untitled Project".
  const words = fold(name).split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 1 || /\d/.test(w));
  const whole = fold(name).trim();
  if (!words.length) return null;
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let best: { node: HTMLElement; length: number } | null = null;
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    const holder = text.parentElement;
    const value = (text.textContent ?? '').trim();
    const folded = fold(value);
    if (!holder || !words.every((w) => folded.includes(w)) || !visible(holder) || outsideWork(holder)) continue;
    if (holder.closest('textarea, [contenteditable="true"]')) continue;
    // The name as written beats any longer text holding the same words.
    const length = folded.trim() === whole ? 0 : value.length;
    if (!best || length < best.length) best = { node: holder, length };
  }
  // A name held in a field (a Factory folder's): the card around it is what opens, the field only edits.
  for (const field of document.querySelectorAll<HTMLInputElement>('input:not([type=password]):not([type=hidden])')) {
    const folded = fold(field.value);
    if (!words.every((w) => folded.includes(w)) || !visible(field) || outsideWork(field)) continue;
    let card = field.parentElement;
    while (card && getComputedStyle(card).cursor !== 'pointer') card = card.parentElement;
    const length = folded.trim() === whole ? 0 : field.value.length;
    if (card && (!best || length < best.length)) best = { node: card, length };
  }
  if (!best) return null;
  const target = best.node.closest<HTMLElement>('button, a, [role="button"], [role="row"], [data-testid], [tabindex]') ?? best.node;
  const label = `${target.getAttribute('aria-label') ?? ''} ${target.title ?? ''}`;
  return DANGEROUS.test(label) ? best.node : target;
};

/** The list's own search box, when the item she looks for is not in view. */
export const findSearchField = () =>
  [...document.querySelectorAll<HTMLInputElement>('input')]
    .find((input) => (input.type === 'search' || /search|recherch/i.test(input.placeholder)) && visible(input) && !outsideWork(input)) ?? null;

/** What the user sees in the work area, for her to read back: capped, whitespace folded. */
export const readScreen = (limit = 3500) => {
  const sidebar = sidebarOf();
  const root = sidebar?.parentElement ?? document.body;
  const parts = [...root.children]
    .filter((child) => child !== sidebar && !child.contains(sidebar ?? null) && visible(child))
    .map((child) => (child as HTMLElement).innerText);
  // Panels, menus and dialogs open in a layer of their own, outside the app's tree: read them first.
  const layers = [...document.body.children]
    .filter((child) => !child.contains(root) && visible(child) && !child.matches('script, style, [data-daimon-ui]') && !child.querySelector('[data-daimon-ui]'))
    .map((child) => (child as HTMLElement).innerText)
    .filter((text) => text.trim());
  parts.unshift(...layers);
  const fields = [...root.querySelectorAll<HTMLInputElement>('input:not([type=password]):not([type=hidden]):not([type=checkbox]):not([type=file]), textarea')]
    .filter((field) => field.value.trim() && visible(field) && !outsideWork(field) && !field.closest('[data-daimon-ui]'))
    .map((field) => `${field.getAttribute('aria-label') || field.placeholder || 'field'}: ${field.value.replace(/\s+/g, ' ').slice(0, 120)}`);
  if (fields.length) parts.push(`Fields: ${fields.slice(0, 30).join(' | ')}`);
  const text = (parts.join('\n') || document.body.innerText).replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

/** A row of controls read as the user sees it: a field's typed value counts, which innerText omits. */
export const readControls = (row: Element) =>
  [...row.children]
    .map((child) => {
      const field = child.querySelector('input');
      return [field?.value, (child as HTMLElement).innerText].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
    })
    .filter(Boolean)
    .join(' · ');

const INTERACTIVE = 'button, a[href], select, summary, input[type="checkbox"], [role="button"], [role="menuitem"], [role="menuitemradio"], [role="tab"], [role="option"], [role="checkbox"], [role="switch"]';

// What the user reads first: the aria label, else the visible text; a title is only a tooltip,
// often the same on every row ('Make this the active model' on each model), so it comes last.
// innerText omits what a field holds, and some names are fields (a Factory folder's name is an
// editable input inside its card): she read 'no folder' with WESTFIELD BRAND on screen.
const fieldText = (node: HTMLElement) =>
  node.matches('input, textarea') ? (node as HTMLInputElement).value : [...node.querySelectorAll<HTMLInputElement>('input:not([type=password]):not([type=hidden]), textarea')].map((f) => f.value).join(' ');
const labelOf = (node: HTMLElement) =>
  (node.getAttribute('aria-label') || [node.innerText, fieldText(node)].filter((t) => t.trim()).join(' ') || node.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
const everythingSaid = (node: HTMLElement) =>
  [node.getAttribute('aria-label'), node.innerText, fieldText(node), node.getAttribute('title')].filter(Boolean).join(' ').replace(/\s+/g, ' ');

// Many rows here are plain elements with a click handler (a model in the model panel): what the
// user reads as clickable, the pointer cursor, counts too, taken at its outermost element.
const pointerRows = () =>
  [...document.querySelectorAll<HTMLElement>('div, li, span, label, img, article, figure, tr')].filter((node) => {
    if (getComputedStyle(node).cursor !== 'pointer') return false;
    const parent = node.parentElement;
    return !parent || getComputedStyle(parent).cursor !== 'pointer';
  });

const controls = () =>
  [...new Set([...document.querySelectorAll<HTMLElement>(INTERACTIVE), ...pointerRows()])]
    .filter((node) => visible(node) && !outsideWork(node) && labelOf(node) && labelOf(node).length <= 80);

/** The label she pressed, as she would read it back. */
export const controlLabel = (node: Element) => (node instanceof HTMLElement ? labelOf(node).slice(0, 60) : '');

/** What a press changed, read as lines newly on screen: 'Done' alone let her claim what she hoped. */
export const screenChange = (before: string, after: string, limit = 700) => {
  const seen = new Set(before.split('\n').map((line) => line.trim()));
  const added = after.split('\n').map((line) => line.trim()).filter((line) => line && !seen.has(line)).join(' | ');
  return added ? (added.length > limit ? `${added.slice(0, limit)}…` : added) : 'nothing new';
};

/** The labels of what can be pressed on screen, menus included, so she knows what she can click. */
export const listControls = (limit = 80) => [...new Set(controls().map((node) => labelOf(node).slice(0, 40)))].slice(0, limit).join(' | ');

/** A control by its label: an exact label first, else every word of it, the shortest label winning. */
export const findControl = (label: string): HTMLElement | null => {
  const wanted = fold(label).trim();
  const words = wanted.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (!words.length) return null;
  const all = controls().map((node) => ({ node, text: fold(labelOf(node)), said: fold(everythingSaid(node)) }));
  return all.find((c) => c.text === wanted)?.node
    ?? all.filter((c) => words.every((w) => c.said.includes(w))).sort((a, b) => a.text.length - b.text.length)[0]?.node
    ?? null;
};

export const isDangerous = (node: HTMLElement) => DANGEROUS.test(labelOf(node));

/** A click as the user's mouse makes it, at a point: pointer, mouse and click (or the right-click's menu). */
export const clickAt = (target: Element, x: number, y: number, kind: 'left' | 'right' | 'double') => {
  const button = kind === 'right' ? 2 : 0;
  const base = { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, button, buttons: button === 2 ? 2 : 1, view: window };
  target.dispatchEvent(new PointerEvent('pointerdown', { ...base, pointerId: 1, pointerType: 'mouse', isPrimary: true }));
  target.dispatchEvent(new MouseEvent('mousedown', base));
  target.dispatchEvent(new PointerEvent('pointerup', { ...base, buttons: 0, pointerId: 1, pointerType: 'mouse', isPrimary: true }));
  target.dispatchEvent(new MouseEvent('mouseup', { ...base, buttons: 0 }));
  if (kind === 'right') { target.dispatchEvent(new MouseEvent('contextmenu', { ...base, buttons: 0 })); return; }
  if (target instanceof HTMLElement) target.click(); else target.dispatchEvent(new MouseEvent('click', { ...base, buttons: 0 }));
  if (kind === 'double') {
    if (target instanceof HTMLElement) target.click();
    target.dispatchEvent(new MouseEvent('dblclick', { ...base, buttons: 0, detail: 2 }));
  }
};

/** Keys she may press: moving through pages and lists, closing, confirming, zooming. */
export const scrollWork = (direction: 'up' | 'down') => {
  const region = [...document.querySelectorAll<HTMLElement>('*')]
    .filter((node) => node.scrollHeight > node.clientHeight + 40 && /(auto|scroll)/.test(getComputedStyle(node).overflowY) && visible(node) && !outsideWork(node))
    .sort((a, b) => b.clientHeight * b.clientWidth - a.clientHeight * a.clientWidth)[0];
  const amount = (region?.clientHeight ?? window.innerHeight) * 0.8 * (direction === 'down' ? 1 : -1);
  if (region) region.scrollBy({ top: amount, behavior: 'smooth' }); else window.scrollBy({ top: amount, behavior: 'smooth' });
};

/**
 * A point on the empty canvas, beside the frames (where a right-click offers 'Add Workspace'):
 * the largest canvas on screen, then the space around it inside its stage, first free spot wins.
 */
export const emptyCanvasPoint = (): { x: number; y: number; target: Element } | null => {
  const canvas = [...document.querySelectorAll('canvas')].filter((node) => visible(node) && !outsideWork(node))
    .sort((a, b) => b.clientWidth * b.clientHeight - a.clientWidth * a.clientHeight)[0];
  if (!canvas) return null;
  const c = canvas.getBoundingClientRect();
  let stage: HTMLElement | null = canvas.parentElement;
  while (stage && stage.getBoundingClientRect().width < c.width + 80) stage = stage.parentElement;
  if (!stage) return null;
  const st = stage.getBoundingClientRect();
  const spots = [
    [(c.right + st.right) / 2, c.top + c.height / 2], [(c.left + st.left) / 2, c.top + c.height / 2],
    [c.left + c.width / 2, (c.top + st.top) / 2], [c.left + c.width / 2, (c.bottom + st.bottom) / 2],
  ];
  for (const [x, y] of spots) {
    const target = document.elementFromPoint(x!, y!);
    if (target && stage.contains(target) && target.tagName !== 'CANVAS' && !target.closest(INTERACTIVE) && !outsideWork(target)) return { x: x!, y: y!, target };
  }
  return { x: c.left + c.width / 2, y: c.top + c.height / 2, target: canvas };
};

// --- Her hands beyond a click (owner, 2026-10-02: 'the Daimon must be able to do literally
// anything a user can do on the app'): any field, any key, a drag, a hover, a file, a panel's scroll.

const FIELDS = 'input:not([type=hidden]):not([type=password]):not([type=file]), textarea, select, [contenteditable="true"], [contenteditable=""]';

/** What a field is called on screen: its aria label, its <label>, its placeholder, the text just before it. */
const fieldName = (field: HTMLElement) => {
  const own = field.getAttribute('aria-label') || (field as HTMLInputElement).placeholder || '';
  const byFor = field.id ? document.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent : '';
  const wrapping = field.closest('label')?.textContent ?? '';
  const before = field.previousElementSibling instanceof HTMLElement ? field.previousElementSibling.innerText : '';
  const row = field.parentElement?.previousElementSibling instanceof HTMLElement ? field.parentElement.previousElementSibling.innerText : '';
  return [own, byFor, wrapping, before, row, field.getAttribute('name') ?? ''].filter((t) => t?.trim()).join(' ').replace(/\s+/g, ' ').trim();
};

const fields = () => [...document.querySelectorAll<HTMLElement>(FIELDS)].filter((f) => visible(f) && !outsideWork(f) && !(f as HTMLInputElement).disabled);

/** The fields on screen as she can name them, with what each holds. */
export const listFields = (limit = 40) => fields().slice(0, limit)
  .map((f) => `${fieldName(f).slice(0, 50) || f.tagName.toLowerCase()}${f instanceof HTMLSelectElement ? ` (choices: ${[...f.options].map((o) => o.text.trim()).slice(0, 12).join(', ')})` : ''}${f instanceof HTMLInputElement && f.type !== 'text' ? ` [${f.type}]` : ''}${'value' in f && (f as HTMLInputElement).value ? ` = ${String((f as HTMLInputElement).value).slice(0, 40)}` : ''}`)
  .join(' | ');

/** A field by the name the user would give it; 'focused' is the one holding the caret. */
export const findField = (name: string): HTMLElement | null => {
  const active = document.activeElement as HTMLElement | null;
  if (/^(focused|current|active|this)$/i.test(name.trim())) return active?.matches(FIELDS) ? active : null;
  const wanted = fold(name).trim();
  const words = wanted.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (!words.length) return null;
  const all = fields().map((node) => ({ node, said: fold(`${fieldName(node)} ${(node as HTMLInputElement).value ?? ''}`) }));
  return all.find((f) => fold(fieldName(f.node)) === wanted)?.node
    ?? all.filter((f) => words.every((w) => f.said.includes(w))).sort((a, b) => a.said.length - b.said.length)[0]?.node
    ?? null;
};

const nativeSet = (field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, value: string) => {
  const proto = field instanceof HTMLSelectElement ? HTMLSelectElement.prototype : field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(field, value);
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
};

/** True when the field takes typed words (text, search, a cell, a rich editor) rather than a choice. */
export const isTypingField = (field: HTMLElement) =>
  field instanceof HTMLTextAreaElement || field.isContentEditable
  || (field instanceof HTMLInputElement && ['text', 'search', 'email', 'url', 'tel', ''].includes(field.type));

/** Sets a choice field (a list, a slider, a number, a date, a colour, a box to tick); null when done, else why not. */
export const setChoice = (field: HTMLElement, value: string): string | null => {
  if (field instanceof HTMLSelectElement) {
    const wanted = fold(value).trim();
    const option = [...field.options].find((o) => fold(o.text).trim() === wanted || fold(o.value) === wanted)
      ?? [...field.options].find((o) => fold(o.text).includes(wanted));
    if (!option) return `No choice "${value}" here. The choices: ${[...field.options].map((o) => o.text.trim()).join(', ')}.`;
    nativeSet(field, option.value);
    return null;
  }
  if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) {
    const on = !/^(off|no|false|non|uncheck|untick|décoch|0)/i.test(value.trim());
    if (field.checked !== on) field.click();
    return null;
  }
  if (field instanceof HTMLInputElement) { nativeSet(field, value); return null; }
  return 'This field takes typed text.';
};

const KEY_NAMES: Record<string, string> = { space: ' ', spacebar: ' ', esc: 'Escape', del: 'Delete', return: 'Enter', up: 'ArrowUp', down: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight', backspace: 'Backspace', delete: 'Delete', tab: 'Tab', enter: 'Enter', escape: 'Escape', home: 'Home', end: 'End', pageup: 'PageUp', pagedown: 'PageDown' };

/** 'Ctrl+Z', 'Shift+ArrowUp', 'Delete', 'v': a key with its modifiers, as the keyboard sends it. */
export const parseKeys = (combo: string) => {
  const parts = combo.split('+').map((p) => p.trim()).filter(Boolean);
  const last = parts.pop() ?? '';
  const mods = parts.map((p) => p.toLowerCase());
  const key = KEY_NAMES[last.toLowerCase()] ?? (/^f\d{1,2}$/i.test(last) ? last.toUpperCase() : last.length === 1 ? last : last[0]!.toUpperCase() + last.slice(1));
  return {
    key,
    ctrlKey: mods.some((m) => /^(ctrl|control)$/.test(m)),
    metaKey: mods.some((m) => /^(cmd|command|meta|win)$/.test(m)),
    shiftKey: mods.includes('shift'),
    altKey: mods.some((m) => /^(alt|option|opt)$/.test(m)),
  };
};

/** Presses a key combination on the page (its shortcuts), or into the field holding the caret when inField. */
export const pressKeys = (combo: string, inField = false) => {
  const keys = parseKeys(combo);
  const active = document.activeElement as HTMLElement | null;
  if (!inField && active?.matches('input, textarea, [contenteditable="true"]')) active.blur();
  const target = (document.activeElement as HTMLElement | null) ?? document.body;
  const code = keys.key.length === 1 && /[a-z]/i.test(keys.key) ? `Key${keys.key.toUpperCase()}` : keys.key.length === 1 && /\d/.test(keys.key) ? `Digit${keys.key}` : keys.key === ' ' ? 'Space' : keys.key;
  for (const type of ['keydown', 'keyup']) target.dispatchEvent(new KeyboardEvent(type, { ...keys, code, bubbles: true, cancelable: true, composed: true }));
};

const pointerInit = (x: number, y: number, buttons: number) => ({ bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, button: 0, buttons, view: window, pointerId: 1, pointerType: 'mouse', isPrimary: true });

/** Hovers a point: what opens on hover (a menu, a tip, a card's buttons) opens. */
export const hoverAt = (x: number, y: number) => {
  const target = document.elementFromPoint(x, y);
  if (!target) return null;
  for (const type of ['pointerover', 'pointerenter', 'pointermove']) target.dispatchEvent(new PointerEvent(type, pointerInit(x, y, 0)));
  for (const type of ['mouseover', 'mouseenter', 'mousemove']) target.dispatchEvent(new MouseEvent(type, pointerInit(x, y, 0)));
  return target;
};

/**
 * Presses at one point, moves along the way and lets go at the other, as a hand drags: a layer moved,
 * a frame or a mask drawn, a row reordered. An element marked draggable gets the browser's own
 * drag-and-drop events instead, which is what such lists listen to.
 */
export const dragBetween = async (from: { x: number; y: number }, to: { x: number; y: number }, onStep: (x: number, y: number) => Promise<boolean>, steps = 16) => {
  const source = document.elementFromPoint(from.x, from.y);
  if (!source) return false;
  const draggable = source.closest<HTMLElement>('[draggable="true"]');
  if (draggable) {
    const data = new DataTransfer();
    draggable.dispatchEvent(new DragEvent('dragstart', { ...pointerInit(from.x, from.y, 1), dataTransfer: data }));
    for (let i = 1; i <= steps; i++) {
      const x = from.x + ((to.x - from.x) * i) / steps; const y = from.y + ((to.y - from.y) * i) / steps;
      if (!(await onStep(x, y))) { draggable.dispatchEvent(new DragEvent('dragend', { ...pointerInit(x, y, 0), dataTransfer: data })); return false; }
      document.elementFromPoint(x, y)?.dispatchEvent(new DragEvent(i === 1 ? 'dragenter' : 'dragover', { ...pointerInit(x, y, 1), dataTransfer: data }));
    }
    document.elementFromPoint(to.x, to.y)?.dispatchEvent(new DragEvent('drop', { ...pointerInit(to.x, to.y, 0), dataTransfer: data }));
    draggable.dispatchEvent(new DragEvent('dragend', { ...pointerInit(to.x, to.y, 0), dataTransfer: data }));
    return true;
  }
  source.dispatchEvent(new PointerEvent('pointerdown', pointerInit(from.x, from.y, 1)));
  source.dispatchEvent(new MouseEvent('mousedown', pointerInit(from.x, from.y, 1)));
  for (let i = 1; i <= steps; i++) {
    const x = from.x + ((to.x - from.x) * i) / steps; const y = from.y + ((to.y - from.y) * i) / steps;
    if (!(await onStep(x, y))) {
      source.dispatchEvent(new PointerEvent('pointerup', pointerInit(x, y, 0)));
      source.dispatchEvent(new MouseEvent('mouseup', pointerInit(x, y, 0)));
      return false;
    }
    // The pressed element keeps the gesture (pointer capture), and listeners on the window see it bubble.
    source.dispatchEvent(new PointerEvent('pointermove', pointerInit(x, y, 1)));
    source.dispatchEvent(new MouseEvent('mousemove', pointerInit(x, y, 1)));
  }
  source.dispatchEvent(new PointerEvent('pointerup', pointerInit(to.x, to.y, 0)));
  source.dispatchEvent(new MouseEvent('mouseup', pointerInit(to.x, to.y, 0)));
  return true;
};

/** Hands files to a file picker or drops them where a user would drop them. */
export const dropFiles = (target: Element, files: File[], x: number, y: number) => {
  const input = target instanceof HTMLInputElement && target.type === 'file'
    ? target
    : target.querySelector<HTMLInputElement>('input[type=file]') ?? target.closest('label, [role="button"], button')?.querySelector<HTMLInputElement>('input[type=file]') ?? null;
  const data = new DataTransfer();
  for (const file of files) data.items.add(file);
  if (input) {
    input.files = data.files;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return 'picker';
  }
  for (const type of ['dragenter', 'dragover', 'drop']) target.dispatchEvent(new DragEvent(type, { ...pointerInit(x, y, 0), dataTransfer: data }));
  return 'drop';
};

/** The scrollable panel holding a named thing, for 'scroll the Recents' rather than the page. */
export const scrollWithin = (name: string, direction: 'up' | 'down') => {
  const anchor = findControl(name) ?? findItem(name) ?? findField(name);
  let node: HTMLElement | null = anchor;
  while (node && !(node.scrollHeight > node.clientHeight + 40 && /(auto|scroll)/.test(getComputedStyle(node).overflowY))) node = node.parentElement;
  if (!node) return false;
  node.scrollBy({ top: node.clientHeight * 0.8 * (direction === 'down' ? 1 : -1), behavior: 'smooth' });
  return true;
};
